import { useMemo } from 'react';
import { Folder } from 'lucide-react';
import { AGENT_GROUP_NAME_MAX_LENGTH, agentGroupRef } from '@reflex/shared';
import {
  useAddAgentsToGroup,
  useAgentGroups,
  useCreateAgentGroup,
} from '@/hooks/agents/useAgentGroups';
import { TaxonomyPickerDialog } from '@/components/agent/TaxonomyPickerDialog';
import {
  groupActionLabel,
  isFiledInAGroup,
  type GroupTargetAgent,
} from '@/components/agent/groupActionLabel';

export interface AddToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The agents to file. Usually a multi-selection; a row menu passes one. */
  agents: GroupTargetAgent[];
  /** Fired after a successful write, so a caller can leave select mode. */
  onAdded?: () => void;
}

/**
 * Picks the group a set of agents goes into, or names a new one. The control
 * is the shared {@link TaxonomyPickerDialog}; this wires it to the group
 * catalog and keeps "create it and file the selection into it" a single call,
 * so there is no window where a named group exists with nothing in it.
 *
 * Group membership is exclusive, so picking a group moves the selection out
 * of whatever group it was in. The dialog says so rather than letting the
 * write surprise you: it is titled "Move to group" once anything selected is
 * already filed, and the group they are all already in is marked as the
 * current one and cannot be picked.
 */
export function AddToGroupDialog({ open, onOpenChange, agents, onAdded }: AddToGroupDialogProps) {
  const { data: groups = [] } = useAgentGroups();
  const addMembers = useAddAgentsToGroup();
  const createGroup = useCreateAgentGroup();

  const agentIds = useMemo(() => agents.map((agent) => agent.id), [agents]);
  // Only a group every selected agent is already in is a no-op; with a mixed
  // selection the pick still moves the rest, so it stays pickable.
  const currentIds = useMemo(() => {
    const first = agents[0] ? agentGroupRef(agents[0]) : null;
    if (!first) return [];
    return agents.every((agent) => agentGroupRef(agent)?.id === first.id) ? [first.id] : [];
  }, [agents]);
  const moving = isFiledInAGroup(agents);

  const finish = () => {
    onOpenChange(false);
    onAdded?.();
  };

  return (
    <TaxonomyPickerDialog
      open={open}
      onOpenChange={onOpenChange}
      agentCount={agents.length}
      title={groupActionLabel(agents)}
      description={
        moving
          ? 'Choose a group, or type a new name to create one. A session is in one group at a time.'
          : 'Choose a group, or type a new name to create one.'
      }
      icon={<Folder className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
      items={groups}
      currentIds={currentIds}
      pending={addMembers.isPending || createGroup.isPending}
      maxNameLength={AGENT_GROUP_NAME_MAX_LENGTH}
      emptyHint="Type a name to create your first group."
      searchPlaceholder="Find a group or create a new one…"
      searchLabel="Search or name a new group"
      testIdPrefix="add-to-group"
      onSelect={(groupId) => {
        if (agentIds.length === 0) return;
        addMembers.mutate({ groupId, agentIds, moved: moving }, { onSuccess: finish });
      }}
      onCreate={(name) => createGroup.mutate({ name, agentIds }, { onSuccess: finish })}
    />
  );
}
